import axios from 'axios';
import type { StoryboardInputBody, StoryboardInputRecord } from '@agentic-ivy/shared-types';
import api from './api';

export interface StoryboardVersion {
  version: number;
  createdAt: string;
  changeSummary: string | null;
  viewUrl: string;
}

export interface StoryboardInfo {
  versions: StoryboardVersion[];
  current: number | null;
}

interface StoryboardResponse {
  storyboard: { currentVersion: number } | null;
  versions: StoryboardVersion[];
}

const EMPTY: StoryboardInfo = { versions: [], current: null };
const base = (conversationId: string) => `/conversations/${conversationId}`;

export const storyboardApi = {
  async get(conversationId: string): Promise<StoryboardInfo> {
    try {
      const { data } = await api.get<StoryboardResponse>(`${base(conversationId)}/storyboard`);
      return { versions: data.versions ?? [], current: data.storyboard?.currentVersion ?? null };
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return EMPTY;
      throw err;
    }
  },

  async postInput(conversationId: string, body: StoryboardInputBody): Promise<{ inputId: string }> {
    return (await api.post(`${base(conversationId)}/storyboard/inputs`, body)).data;
  },

  async listInputs(conversationId: string): Promise<StoryboardInputRecord[]> {
    return (await api.get<{ inputs: StoryboardInputRecord[] }>(`${base(conversationId)}/storyboard/inputs`)).data.inputs;
  },

  async getInput(conversationId: string, inputId: string): Promise<StoryboardInputRecord> {
    return (await api.get(`${base(conversationId)}/storyboard/inputs/${inputId}`)).data;
  },

  async signMedia(conversationId: string, keys: string[]): Promise<Record<string, string>> {
    const { data } = await api.get<{ urls: Record<string, string> }>(`${base(conversationId)}/media/sign`, {
      params: { keys: keys.join(',') },
    });
    return data.urls;
  },
};

export type StoryboardApi = typeof storyboardApi;
